import { AngularFireAnalytics } from '@newgenus/fire-compat/analytics';
import { AuthService } from '../../shared/services/auth.service';
import { LastRouteService } from '../../shared/services/last-route.service';
import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import firebase from 'firebase/compat/app';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PendingLink, completePendingLink, pendingLinkMessage, toPendingLink } from './pending-link';

@Component({
    selector: 'newgenus-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false,
})
export class LoginComponent implements OnDestroy {
    usernameValue: string | null = null;
    passwordValue: string | null = null;
    public isLoginRunning = false;
    public errorLoggingIn = false;
    /** In-page text shown instead of alert() where we handle the error ourselves. */
    public errorText: string | null = null;
    public infoText: string | null = null;
    /** Provider credential waiting to be linked once the user proves they own the password account. */
    private pendingLink: PendingLink | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        public auth: AuthService,
        private analytics: AngularFireAnalytics,
        private router: Router,
        private lastRouteService: LastRouteService,
        private snackBar: MatSnackBar
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public login() {
        if (this.usernameValue && this.passwordValue) {
            this.isLoginRunning = true;
            this.errorLoggingIn = false;
            this.errorText = null;

            this.analytics.logEvent('login_regular_attempt', {
                event_category: 'Authentication',
                event_label: 'Login Screen',
            });
            this.auth
                .signInRegular(this.usernameValue, this.passwordValue)
                .then(async () => {
                    this.analytics.logEvent('login_regular_success', {
                        event_category: 'Authentication',
                        event_label: 'Login Screen',
                    });
                    await this.linkPendingProvider();
                    this.isLoginRunning = false;
                    this.navigateToLastRoute();
                })
                .catch((err) => {
                    this.isLoginRunning = false;
                    this.errorLoggingIn = true;
                    alert('Sign in failed! Please check your username and password');

                    this.analytics.logEvent('login_regular_failure', {
                        event_category: 'Authentication',
                        event_label: 'Login Screen',
                    });
                });
        } else {
            alert('Please enter username and password');
        }
    }

    public loginViaGoogle(): void {
        this.isLoginRunning = true;
        this.errorLoggingIn = false;
        this.errorText = null;

        this.analytics.logEvent('login_google_attempt', {
            event_category: 'Authentication',
            event_label: 'Login Screen',
        });
        this.auth
            .googleLogin()
            .then(() => {
                this.analytics.logEvent('login_google_success', {
                    event_category: 'Authentication',
                    event_label: 'Login Screen',
                });
                this.onSignInSuccess();
                this.isLoginRunning = false;
            })
            .catch((err) => {
                this.isLoginRunning = false;
                if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
                    // User closed the popup — not a real error, just reset state
                    return;
                }
                this.analytics.logEvent('login_google_failure', {
                    event_category: 'Authentication',
                    event_label: 'Login Screen',
                });
                if (this.handleAccountExists(err, 'Google')) {
                    return;
                }
                this.errorLoggingIn = true;
                alert('Sign in failed! Please check your username and password');
            });
    }

    public loginViaMicrosoft(): void {
        this.isLoginRunning = true;
        this.errorLoggingIn = false;
        this.errorText = null;
        this.analytics.logEvent('login_microsoft_attempt', {
            event_category: 'Authentication',
            event_label: 'Login Screen',
        });
        this.auth
            .microsoftLogin()
            .then(() => {
                // Normal popup success (no COOP issue)
                this.analytics.logEvent('login_microsoft_success', {
                    event_category: 'Authentication',
                    event_label: 'Login Screen',
                });
                this.onSignInSuccess();
                this.isLoginRunning = false;
            })
            .catch((err) => {
                if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
                    // COOP blocks popup→opener communication. The popup completed auth on Firebase's
                    // side but can't send the result back. currentUser is NOT available yet —
                    // we need to wait for onAuthStateChanged to fire once Firebase processes the token.
                    console.log('[LOGIN] Microsoft popup-closed — waiting for auth state to propagate...');

                    const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
                        if (user) {
                            unsubscribe();
                            clearTimeout(authTimeout);
                            console.log('[LOGIN] Microsoft COOP fallback: user authenticated, uid =', user.uid);
                            try {
                                await this.auth.processCurrentUser('microsoft');
                            } catch (e) {
                                console.warn('[LOGIN] processCurrentUser error (non-fatal for existing users):', e);
                            }
                            this.analytics.logEvent('login_microsoft_success', {
                                event_category: 'Authentication',
                                event_label: 'Login Screen (COOP fallback)',
                            });
                            this.isLoginRunning = false;
                            // Navigate to /logging-in which waits for user$ (Firestore profile)
                            // to fully load, then routes to /app/home
                            this.router.navigate(['logging-in']);
                        }
                    });

                    // Timeout: if no auth state after 15 seconds, user genuinely cancelled
                    const authTimeout = setTimeout(() => {
                        unsubscribe();
                        console.log('[LOGIN] Microsoft: timed out waiting for auth state — popup was cancelled');
                        this.isLoginRunning = false;
                    }, 15000);

                    return;
                }
                this.isLoginRunning = false;
                console.error('Microsoft login error:', err?.code, err?.message, err);
                this.analytics.logEvent('login_microsoft_failure', {
                    event_category: 'Authentication',
                    event_label: 'Login Screen',
                });
                if (this.handleAccountExists(err, 'Microsoft')) {
                    return;
                }
                this.errorLoggingIn = true;
                this.errorText = `Microsoft sign in failed: ${err?.code || err?.message || 'Unknown error'}`;
            });
    }

    /**
     * The email already belongs to a password account (Firebase links by email). Keep the provider
     * credential, ask for the password, and link it after that sign-in succeeds (see login()).
     */
    private handleAccountExists(err: any, providerLabel: string): boolean {
        const pending = toPendingLink(err, providerLabel);
        if (!pending) {
            return false;
        }
        this.pendingLink = pending;
        this.errorLoggingIn = false;
        this.errorText = null;
        this.infoText = pendingLinkMessage(pending);
        if (pending.email) {
            this.usernameValue = pending.email;
        }
        return true;
    }

    private async linkPendingProvider(): Promise<void> {
        const pending = this.pendingLink;
        if (!pending) {
            return;
        }
        this.pendingLink = null;
        this.infoText = null;
        const result = await completePendingLink(firebase.auth().currentUser, pending);
        this.snackBar.open(result.message, 'Close', { duration: 6000 });
    }

    private onSignInSuccess() {
        this.navigateToLastRoute();
    }

    private async navigateToLastRoute() {
        // Get current user's uid to verify localStorage ownership
        const uid = firebase.auth().currentUser?.uid;
        const lastUrl = await this.lastRouteService.getLastUrl(uid);
        if (lastUrl) {
            this.router.navigateByUrl(lastUrl);
        } else {
            this.router.navigate(['app/home']);
        }
    }

    public forgotPassword($event: any) {
        // console.log('forgot password called');
    }
}
